import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2, Briefcase, FileText, PlusCircle, MessageCircle, ChevronRight,
} from "lucide-react";
import { getToken, fetchHome, formatEuro } from "@/lib/mastroAuth";
import { InstallSuggestion } from "@/components/InstallSuggestion";
import { DASHBOARD } from "@/constants/testIds";

const money = (v) => formatEuro(v);

// Presentation-layer only: italian labels for the statuses shown on Home.
// The shared <StatusBadge> stays untouched; here we render a local badge with
// the same visual tones so no enum RAW ever appears on Home.
const HOME_STATUS_LABELS = {
  ACTIVE: "In corso",
  CONFIRMED: "Confermato",
  PROPOSED: "Da confermare",
  REJECTED: "Rifiutato",
};
const HOME_STATUS_TONES = {
  ACTIVE: "positive",
  CONFIRMED: "positive",
  PROPOSED: "pending",
  REJECTED: "neutral",
};
const HOME_BADGE_STYLES = {
  positive: "bg-teal-50 text-teal-700 ring-1 ring-inset ring-teal-600/20",
  pending: "bg-white text-neutral-700 ring-1 ring-inset ring-neutral-300",
  neutral: "bg-neutral-100 text-neutral-500 ring-1 ring-inset ring-neutral-200",
};
const HomeStatusBadge = ({ status }) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold tracking-wide ${
      HOME_BADGE_STYLES[HOME_STATUS_TONES[status]] || HOME_BADGE_STYLES.neutral
    }`}
  >
    {HOME_STATUS_LABELS[status] || status}
  </span>
);

const Section = ({ title, count, children }) => (
  <section className="mb-6">
    <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
      <div className="flex items-center justify-between bg-neutral-950 px-5 py-4">
        <h2 className="text-base font-bold text-white md:text-lg">{title}</h2>
        {count > 0 && (
          <span className="rounded-full bg-white/10 px-2.5 py-0.5 text-xs font-medium text-neutral-300">
            {count}
          </span>
        )}
      </div>
      {children}
    </div>
  </section>
);

const Empty = ({ testid, text }) => (
  <p data-testid={testid} className="px-5 py-6 text-sm text-neutral-400">
    {text}
  </p>
);

export default function Home() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHome(getToken())
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div data-testid={DASHBOARD.loading} className="flex items-center gap-3 text-neutral-500">
        <Loader2 className="h-5 w-5 animate-spin" /> Caricamento…
      </div>
    );
  }

  // Backend already returns recent_extras sorted by created_at DESC.
  // Presentation-layer safety cap: never render more than 10 rows on Home.
  const visibleExtras = (data?.recent_extras || []).slice(0, 10);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      data-testid={DASHBOARD.home}
    >
      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight">Panoramica</h1>
      </div>

      <InstallSuggestion />

      {data?.is_empty && (
        <div
          data-testid={DASHBOARD.globalEmpty}
          className="rounded-2xl border border-neutral-200 bg-white p-8 text-center"
        >
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-50 text-teal-600">
            <MessageCircle className="h-6 w-6" />
          </span>
          <p className="mt-4 text-sm font-semibold text-neutral-950">
            Ancora nessun dato nel tuo workspace
          </p>
          <p className="mt-1 text-sm text-neutral-500">
            Apri Mastro su WhatsApp e raccontagli un lavoro, invia una foto o
            chiedi un preventivo: comparirà qui.
          </p>
        </div>
      )}

      {!data?.is_empty && (
        <>
          <Section title="Lavori attivi" count={data?.counts?.active_jobs}>
            {data?.active_jobs?.length ? (
              <div className="divide-y divide-neutral-100" data-testid={DASHBOARD.activeJobs}>
                {data.active_jobs.map((j) => (
                  <Link
                    key={j.id}
                    to={`/app/lavori/${j.id}`}
                    className="flex items-center justify-between gap-3 px-5 py-3.5 transition-colors hover:bg-neutral-50 focus-visible:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-500"
                  >
                    <div className="flex min-w-0 items-center gap-2 text-sm font-medium text-neutral-950">
                      <Briefcase className="h-4 w-4 shrink-0 text-neutral-400" />
                      <span className="truncate">{j.title}</span>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <HomeStatusBadge status={j.status} />
                      <ChevronRight className="h-4 w-4 text-neutral-300" />
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <Empty testid={DASHBOARD.activeJobsEmpty} text="Nessun lavoro attivo al momento." />
            )}
          </Section>

          <Section title="Preventivi da confermare" count={data?.counts?.quotes_to_confirm}>
            {data?.quotes_to_confirm?.length ? (
              <div className="divide-y divide-neutral-100" data-testid={DASHBOARD.quotesToConfirm}>
                {data.quotes_to_confirm.map((q) => (
                  <Link
                    key={q.id}
                    to={`/app/preventivi/${q.id}`}
                    className="block px-5 py-3.5 transition-colors hover:bg-neutral-50 focus-visible:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-500"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-2 text-sm font-medium text-neutral-950">
                        <FileText className="h-4 w-4 shrink-0 text-neutral-400" />
                        <span className="truncate">{q.job_title || "Preventivo"}</span>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <span className="text-sm font-semibold text-neutral-950">{money(q.total)}</span>
                        <ChevronRight className="h-4 w-4 text-neutral-300" />
                      </div>
                    </div>
                    <div className="mt-1 text-xs text-neutral-500">Da confermare · v{q.version_number ?? "—"}</div>
                  </Link>
                ))}
              </div>
            ) : (
              <Empty testid={DASHBOARD.quotesToConfirmEmpty} text="Nessun preventivo in attesa di conferma." />
            )}
          </Section>

          <Section title="Extra recenti" count={visibleExtras.length}>
            {visibleExtras.length ? (
              <div className="divide-y divide-neutral-100" data-testid={DASHBOARD.recentExtras}>
                {visibleExtras.map((e) => (
                  <div key={e.id} className="px-5 py-3.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-neutral-950">
                        <PlusCircle className="h-4 w-4 text-neutral-400" />
                        <span className="font-medium">{e.description}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-neutral-600">{money(e.line_total)}</span>
                        <HomeStatusBadge status={e.status} />
                      </div>
                    </div>
                    {e.job_title && <div className="mt-1 text-xs text-neutral-500">{e.job_title}</div>}
                  </div>
                ))}
              </div>
            ) : (
              <Empty testid={DASHBOARD.recentExtrasEmpty} text="Nessun extra registrato di recente." />
            )}
          </Section>
        </>
      )}
    </motion.div>
  );
}
